import { ExternalLink } from "lucide-react";
import { Link, useParams } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks";
import { getPaletteColor } from "@/lib/color-palettes";
import { Avatar } from "@/components/ui";

/**
 * Format a timestamp to a human-readable relative time string.
 * Accepts a simple translate fn to avoid passing the overloaded `t` directly,
 * which triggers a TypeScript compiler crash (TS "Debug Failure" on overload resolution).
 */
function formatRelativeTime(
  timestamp: number,
  translate: (key: string, options?: { count?: number }) => string,
): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return days === 1
      ? translate("common:time.oneDayAgo")
      : translate("common:time.daysAgo_plural", { count: days });
  }
  if (hours > 0) {
    return hours === 1
      ? translate("common:time.oneHourAgo")
      : translate("common:time.hoursAgo_plural", { count: hours });
  }
  if (minutes > 0) {
    return minutes === 1
      ? translate("common:time.oneMinuteAgo")
      : translate("common:time.minutesAgo_plural", { count: minutes });
  }
  return translate("common:time.justNow");
}

/**
 * Generate a consistent color based on a string.
 */
function getColorFromString(str: string): string {
  const hash = str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  // Use framework palette slots 1-8 for avatar colors (distinct, accent-derived)
  const slot = (hash % 8) + 1;
  return getPaletteColor("framework", slot);
}

// ── Ghost empty state ──────────────────────────────────────────

function TeamActivityGhost() {
  const ghostItems = [
    { nameW: "w-24", descW: "w-32", timeW: "w-16" },
    { nameW: "w-20", descW: "w-36", timeW: "w-14" },
    { nameW: "w-28", descW: "w-28", timeW: "w-16" },
    { nameW: "w-22", descW: "w-40", timeW: "w-12" },
    { nameW: "w-26", descW: "w-30", timeW: "w-14" },
  ];

  return (
    <div className="px-4 pb-4 space-y-0 opacity-15">
      {ghostItems.map((item, i) => (
        <div
          key={`ghost-ta-${i.toString()}`}
          className="flex gap-3 py-2.5 border-b border-border last:border-0"
        >
          <div className="w-9 h-9 rounded-full bg-card-hover shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className={`h-3.5 ${item.nameW} bg-card-hover rounded`} />
              <div className={`h-3 ${item.timeW} bg-card-hover rounded`} />
            </div>
            <div className={`h-3 ${item.descW} bg-card-hover rounded`} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Skeleton ────────────────────────────────────────────────────

function TeamActivitySkeleton() {
  return (
    <div className="px-4 pb-4 space-y-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={`activity-skeleton-${i.toString()}`}
          className="flex gap-3 py-2.5 border-b border-border last:border-0 animate-pulse"
        >
          {/* Avatar circle */}
          <div className="w-9 h-9 rounded-full bg-card-hover shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            {/* Name + timestamp row */}
            <div className="flex items-center justify-between gap-2">
              <div className="h-3.5 w-24 bg-card-hover rounded" />
              <div className="h-3 w-16 bg-card-hover rounded" />
            </div>
            {/* Audit name row */}
            <div className="h-3 w-32 bg-card-hover rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────

export function TeamActivity() {
  const { t } = useTranslation(["dashboard", "common"]);
  // Wrap t to avoid TS compiler crash on overloaded signature resolution
  const simpleT = (key: string, options?: { count?: number }) =>
    // biome-ignore lint/suspicious/noExplicitAny: workaround for TS compiler crash on overloaded i18n signature
    t(key as any, options as any);
  const { workspaceSlug } = useParams({ strict: false });

  // Fetch recent workspace activity from dashboard query
  const { data: activities, isLoading } = useWorkspaceQuery(
    api.dashboard.queries.recentActivity.get,
    {},
  );

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:teamActivity.title")}
        </h3>
      </div>
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 min-h-0"
          >
            <TeamActivitySkeleton />
          </motion.div>
        ) : !activities || activities.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex items-center justify-center px-4 pb-3"
          >
            <TeamActivityGhost />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="activity-body px-4 pb-4 flex-1 overflow-y-auto min-h-0"
          >
            {activities.map((item) => (
              <div
                key={`${item.id}-${item.timestamp}`}
                className="flex gap-3 py-2.5 border-b border-border last:border-0"
              >
                <Avatar
                  name={item.userName || "Unknown"}
                  avatarUrl={item.userImageUrl}
                  avatarColor={getColorFromString(item.userName || "Unknown")}
                  size="sm"
                  className="h-9! w-9!"
                />
                <div className="min-w-0 flex-1">
                  {/* Line 1: Name (left) | Time (right) */}
                  <div className="flex items-center justify-between gap-2">
                    <strong className="text-[13px] font-semibold text-primary truncate">
                      {item.userName || "System"}
                    </strong>
                    <span className="text-[11px] text-muted whitespace-nowrap shrink-0">
                      {formatRelativeTime(item.timestamp, simpleT)}
                    </span>
                  </div>
                  {/* Line 2: Audit name (left) | Link icon (right) */}
                  {item.auditName && (
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-muted font-medium truncate">
                        {item.auditName}
                      </span>
                      {item.auditId && workspaceSlug && (
                        <Link
                          to="/$workspaceSlug/audits/$auditId"
                          params={{
                            workspaceSlug,
                            auditId: item.auditId,
                          }}
                          search={{ tab: "activity" }}
                          className="shrink-0 text-muted hover:text-primary transition-colors"
                        >
                          <ExternalLink size={12} strokeWidth={2} />
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
